import React, { createContext, useContext, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUITheme } from '../../hooks/useUIPreferences';
import './WorkspaceUI.css';

export { useUIPreference } from '../../hooks/useUIPreferences';

export function UITheme({ children, className = '', as: Tag = 'div', ...props }) {
  const theme = useUITheme();
  return <Tag {...props} className={`henosis-ui ${className}`} data-theme={theme.resolved} data-theme-choice={theme.choice}>{children}</Tag>;
}

export function ThemeToggle() {
  const theme = useUITheme();
  const id = useId();
  return (
    <div className="ui-theme-control">
      <span aria-hidden="true">◐</span><label className="sr-only" htmlFor={id}>Color theme</label>
      <select id={id} value={theme.choice} onChange={(event) => theme.change(event.target.value)}>
        <option value="system">System</option><option value="light">Light</option><option value="dark">Dark</option>
      </select>
    </div>
  );
}

const dialogs = [];
const DialogDepth = createContext(0);
const inertBefore = new Map();
let overflowBefore;
let originFocus;
let bodyObserver;
const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
const focusableIn = (node) => Array.from(node?.querySelectorAll(focusableSelector) || []).filter((element) => {
  if (element.hasAttribute('tabindex') && element.tabIndex < 0) return false;
  if (element.closest('[hidden],[inert],[aria-hidden="true"]')) return false;
  if (element.checkVisibility && !element.checkVisibility({ checkVisibilityCSS: true })) return false;
  for (let ancestor = element; ancestor && ancestor !== node?.parentElement; ancestor = ancestor.parentElement) {
    const style = getComputedStyle(ancestor);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    if (ancestor.tagName === 'DETAILS' && !ancestor.open && !ancestor.querySelector(':scope > summary')?.contains(element)) return false;
  }
  return true;
});
const canRestore = (element) => element?.isConnected && !element.closest('[inert],[hidden]');

function syncModalBackground() {
  const top = dialogs[dialogs.length - 1];
  if (top) {
    Array.from(document.body.children).forEach((node) => {
      if (!inertBefore.has(node)) inertBefore.set(node, node.hasAttribute('inert'));
      if (node === top.host) node.removeAttribute('inert');
      else node.setAttribute('inert', '');
    });
  } else {
    bodyObserver?.disconnect();
    bodyObserver = null;
    inertBefore.forEach((wasInert, node) => {
      if (wasInert) node.setAttribute('inert', '');
      else node.removeAttribute('inert');
    });
    inertBefore.clear();
    document.body.style.overflow = overflowBefore;
  }
}

// The manager owns the lock until the LAST dialog closes, regardless of cleanup order.
function acquireDialog(entry) {
  if (!dialogs.length) {
    overflowBefore = document.body.style.overflow;
    originFocus = document.activeElement;
    document.body.style.overflow = 'hidden';
    bodyObserver = new MutationObserver(syncModalBackground);
    bodyObserver.observe(document.body, { childList: true });
  }
  dialogs.push(entry);
  dialogs.sort((a, b) => a.depth - b.depth);
  syncModalBackground();
}
function releaseDialog(entry) {
  const wasTop = dialogs[dialogs.length - 1] === entry;
  const index = dialogs.indexOf(entry);
  if (index < 0) return;
  dialogs.splice(index, 1);
  entry.host.remove();
  syncModalBackground();
  if (!dialogs.length) {
    const target = entry.returnFocusRef?.current || originFocus;
    if (canRestore(target)) target.focus();
    else if (canRestore(originFocus)) originFocus.focus();
    originFocus = null;
  } else if (wasTop) {
    const target = entry.returnFocusRef?.current || entry.returnFocus;
    if (canRestore(target)) target.focus();
    else dialogs[dialogs.length - 1].focusFirst();
  }
}

export function Dialog({ open, onClose, title, children, className = '', initialFocusRef, returnFocusRef, labelId, header, panelClassName = '', bodyClassName = '', describedBy }) {
  const depth = useContext(DialogDepth);
  const uniqueId = useId();
  const titleId = labelId || `ui-dialog-${uniqueId}`;
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const initialRef = useRef(initialFocusRef);
  initialRef.current = initialFocusRef;
  const [host] = useState(() => document.createElement('div'));
  useLayoutEffect(() => {
    if (!open) return undefined;
    host.className = 'ui-dialog-portal';
    const entry = {
      host,
      depth,
      returnFocus: document.activeElement,
      returnFocusRef,
      focusFirst: () => (initialRef.current?.current || focusableIn(panelRef.current)[0] || panelRef.current)?.focus(),
    };
    document.body.appendChild(host);
    acquireDialog(entry);
    dialogs[dialogs.length - 1].focusFirst();
    const keydown = (event) => {
      if (event.defaultPrevented || dialogs[dialogs.length - 1] !== entry) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
      } else if (event.key === 'Tab') {
        const nodes = focusableIn(panelRef.current);
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (!first) { event.preventDefault(); panelRef.current?.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    const focusin = (event) => {
      if (dialogs[dialogs.length - 1] === entry && !host.contains(event.target)) entry.focusFirst();
    };
    document.addEventListener('keydown', keydown);
    document.addEventListener('focusin', focusin);
    return () => {
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('focusin', focusin);
      releaseDialog(entry);
    };
  }, [open, host, depth, returnFocusRef]);
  if (!open) return null;
  return createPortal(
    <DialogDepth.Provider value={depth + 1}><UITheme className={`ui-dialog-backdrop ${className}`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <section ref={panelRef} className={`ui-dialog ${panelClassName}`} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={describedBy} tabIndex={-1}>
        {header === undefined ? <header className="ui-dialog-header"><h2 id={titleId}>{title}</h2><button type="button" className="ui-icon-button" aria-label={`Close ${title}`} onClick={onClose}>×</button></header> : header}
        <div className={`ui-dialog-body ${bodyClassName}`}>{children}</div>
      </section>
    </UITheme></DialogDepth.Provider>, host
  );
}
